import classNames from 'classnames';
import { useRef, useState, type ReactNode } from 'react';

export type HBarRow = { key: string; label: string; value: number; muted?: boolean };

/**
 * A single-series horizontal bar chart (dataviz): one hue, so no legend; bars 20px
 * thick, square at the baseline and rounded 4px at the data end, the value at each
 * tip. "Unknown"-style rows can be muted to the de-emphasis grey. Hovering or
 * focusing a row shows its tooltip, and on a touch screen a tap does (UI-1); the page
 * puts the same numbers in a table.
 */
const HBarChart = ({
  rows,
  format,
  label,
  tooltip,
  after,
}: {
  rows: HBarRow[];
  format: (v: number) => string;
  /** What the chart shows, for screen readers. */
  label: string;
  tooltip?: (row: HBarRow) => ReactNode;
  /** A line under a row, such as the conversion to the next funnel step. */
  after?: (row: HBarRow, index: number) => ReactNode;
}) => {
  const [active, setActive] = useState<string | null>(null);
  // A finger leaves the row as soon as it lifts, so only a mouse shows and hides the tooltip by hovering; a tap toggles it.
  const pointer = useRef('mouse');
  const max = Math.max(1, ...rows.map((r) => r.value));

  return (
    <ul aria-label={label} className="space-y-1">
      {rows.map((r, i) => {
        const pct = (r.value / max) * 100;
        const extra = after?.(r, i);
        return (
          <li key={r.key}>
            <div
              tabIndex={0}
              aria-label={`${r.label}: ${format(r.value)}`}
              onPointerDown={(e) => (pointer.current = e.pointerType)}
              onPointerEnter={(e) => e.pointerType === 'mouse' && setActive(r.key)}
              onPointerLeave={(e) => e.pointerType === 'mouse' && setActive(null)}
              onClick={() => pointer.current !== 'mouse' && setActive((a) => (a === r.key ? null : r.key))}
              onFocus={() => setActive(r.key)}
              onBlur={() => setActive(null)}
              className="grid grid-cols-[88px_1fr] items-center gap-3 rounded-md py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 md:grid-cols-[112px_1fr] md:py-1"
            >
              <span className="truncate text-sm text-fg-muted">{r.label}</span>
              {/* The right margin keeps room for the value at the longest bar's tip. */}
              <span className="relative mr-12 h-5">
                <span className="absolute -inset-y-1 left-0 w-px bg-line" aria-hidden />
                <span
                  className={classNames(
                    'absolute inset-y-0 left-0 rounded-r transition-opacity',
                    r.muted ? 'bg-viz-muted' : 'bg-viz-accent',
                    active === r.key && 'opacity-80',
                  )}
                  style={{ width: `${pct}%` }}
                />
                <span className="absolute top-1/2 ml-2 -translate-y-1/2 whitespace-nowrap text-xs text-fg-muted" style={{ left: `${pct}%` }}>
                  {format(r.value)}
                </span>
                {active === r.key && tooltip && (
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute bottom-full z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-surface-raised px-2 py-1 text-xs shadow-e4"
                    style={{ left: `${Math.max(pct, 8)}%` }}
                  >
                    {tooltip(r)}
                  </span>
                )}
              </span>
            </div>
            {extra && <div className="pl-[100px] text-xs text-fg-faint md:pl-[124px]">{extra}</div>}
          </li>
        );
      })}
    </ul>
  );
};

export default HBarChart;
